import { FC, useEffect, useState } from 'react'
import {
  DetailField,
  DetailFields,
  DetailPanel,
  DetailSection,
  StatusDot,
  StatusDotTone,
} from '../../../../../../components'
import { NotaView } from '../../core/_models'
import { showError, StatusType } from '../../../../../../../_metronic/helpers'
import { getTiposStatusNotaByName } from '../../../../../../../_metronic/helpers/crud-helper/_request'
import { useT } from '_metronic/i18n/Metronici18n'

type IProps = {
  data: NotaView
  isLoading?: boolean
  isProfissional?: boolean
}

const statusTone = (status?: StatusType): StatusDotTone => {
  switch (status) {
    case StatusType.Valido:
      return 'success'
    case StatusType.Invalido:
      return 'danger'
    case StatusType.Nao_Encontrado:
      return 'warning'
    default:
      return 'neutral'
  }
}

export const NotaExpandedRow: FC<IProps> = ({ data, isLoading, isProfissional }) => {
  const [statusMap, setStatusMap] = useState<Record<string, string>>({})
  const { t } = useT()

  const validacao = data?.validacao

  useEffect(() => {
    const fetchData = async () => {
      try {
        const statusList = [
          validacao?.statusNota,
          validacao?.statusValor,
          validacao?.statusCnae,
          validacao?.statusData,
        ].filter(Boolean) as StatusType[]

        const statusToFetch = Array.from(new Set(statusList)).filter((status) => !statusMap[status])

        if (statusToFetch.length === 0) return

        const results = await Promise.all(
          statusToFetch.map(async (status) => {
            const resp = await getTiposStatusNotaByName(status)
            return { status, descricao: resp.descricao }
          })
        )

        setStatusMap((prev) => {
          const newMap = { ...prev }
          results.forEach(({ status, descricao }) => {
            newMap[status] = descricao
          })
          return newMap
        })
      } catch (error) {
        showError(error)
      }
    }

    if (validacao) {
      fetchData()
    }
  }, [validacao, statusMap])

  // Situação da validação: rótulo (descrição vinda da API) + tom. O texto carrega o significado.
  const status = (status?: StatusType) =>
    status && <StatusDot label={statusMap[status] || 'Carregando…'} tone={statusTone(status)} />

  return (
    <DetailPanel isLoading={isLoading} loadingLabel='Carregando detalhes da nota…'>
      {data && (
        <>
          <DetailSection title={t('data.plural')}>
            <DetailFields>
              <DetailField label={t('fechamento.label')} value={data.dataFechamentoFormatada} />
              <DetailField label={t('recebimento.label')} value={data.dataRecebimentoFormatada} />
              <DetailField label={t('pagamento.label')} value={data.dataPagamentoFormatada} />
            </DetailFields>
          </DetailSection>

          <DetailFields>
            <DetailField
              label={t('observacao.label')}
              value={data.observacao}
              emptyLabel={t('common.none', { name: t('observacao.label.lowercase') })}
              wide
            />
          </DetailFields>

          {!isProfissional && (
            <DetailSection title={t('validacao_nota.label')}>
              {validacao ? (
                <DetailFields>
                  <DetailField label='CNAE' value={validacao.cnae} />
                  <DetailField label={t('valor.label')} value={validacao.valorFormatado} />
                  <DetailField label={t('data.label')} value={validacao.dataFormatada} />
                  <DetailField
                    label={`${t('situacao.label')} da ${t('nota.label.lowercase')}`}
                    value={status(validacao.statusNota)}
                  />
                  <DetailField
                    label={`${t('situacao.label')} do CNAE`}
                    value={status(validacao.statusCnae)}
                  />
                  <DetailField
                    label={`${t('situacao.label')} do valor`}
                    value={status(validacao.statusValor)}
                  />
                  <DetailField
                    label={`${t('situacao.label')} da ${t('data.label.lowercase')}`}
                    value={status(validacao.statusData)}
                  />
                </DetailFields>
              ) : (
                <span className='text-muted'>
                  {t('common.none', { name: t('dado.plural.lowercase') })}
                </span>
              )}
            </DetailSection>
          )}
        </>
      )}
    </DetailPanel>
  )
}
